import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Search, MapPin, Dumbbell, ArrowUpDown, X } from 'lucide-react';
import VenueCard from './VenueCard';
import VenueDetailModal from './VenueDetailModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRICE_RANGES = [
  { key: '', label: 'كل الأسعار' },
  { key: 'lt100', label: 'أقل من 100', max: 100 },
  { key: '100-300', label: '100 - 300', min: 100, max: 300 },
  { key: 'gt300', label: 'أكثر من 300', min: 300 },
];
const SORT_OPTIONS = [
  { key: '', label: 'الأحدث' },
  { key: 'price_asc', label: 'الأقل سعرًا' },
  { key: 'rating_desc', label: 'الأعلى تقييمًا' },
];

const SelectFilter = ({ icon: IconEl, value, onChange, children, testId }) => (
  <div className="flex shrink-0 items-center gap-1.5 rounded-full border border-white/10 bg-[#1E2D4A] px-3 py-2 whitespace-nowrap">
    <IconEl className="h-3.5 w-3.5 shrink-0 text-slate-400" />
    <select value={value} onChange={onChange} data-testid={testId} className="bg-transparent text-xs font-semibold text-white focus:outline-none">
      {children}
    </select>
  </div>
);

export function RealVenuesSection() {
  const [venues, setVenues] = useState(null);
  const [cities, setCities] = useState([]);
  const [sportTypes, setSportTypes] = useState([]);
  const [search, setSearch] = useState('');
  const [city, setCity] = useState('');
  const [sportType, setSportType] = useState('');
  const [priceKey, setPriceKey] = useState('');
  const [womenOnly, setWomenOnly] = useState(false);
  const [sort, setSort] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [selectedVenue, setSelectedVenue] = useState(null);

  const hasActiveFilters = Boolean(search || city || sportType || priceKey || womenOnly || sort);

  const loadVenues = useCallback(async () => {
    const range = PRICE_RANGES.find((r) => r.key === priceKey);
    try {
      const res = await axios.get(`${API}/public/venues`, {
        params: {
          search: search || undefined, city: city || undefined, sport_type: sportType || undefined,
          women_friendly: womenOnly || undefined, min_price: range?.min, max_price: range?.max,
          sort: sort || undefined,
        },
      });
      setVenues(res.data || []);
    } catch { setVenues([]); }
  }, [search, city, sportType, priceKey, womenOnly, sort]);

  useEffect(() => { loadVenues(); }, [loadVenues]);
  useEffect(() => { axios.get(`${API}/public/venues/cities`).then((r) => setCities(r.data || [])).catch(() => setCities([])); }, []);
  useEffect(() => { axios.get(`${API}/public/venues/sport-types`).then((r) => setSportTypes(r.data || [])).catch(() => setSportTypes([])); }, []);

  const clearFilters = () => { setSearch(''); setCity(''); setSportType(''); setPriceKey(''); setWomenOnly(false); setSort(''); };

  const openVenue = async (id) => {
    setSelectedId(id);
    try {
      const res = await axios.get(`${API}/public/venues/${id}`);
      setSelectedVenue(res.data);
    } catch { setSelectedId(null); }
  };

  return (
    <div id="real-venues-section">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-2xl font-bold text-white">ملاعب منشآت تسيار، بأفضل صورة</h3>
          <p className="mt-1 text-sm text-slate-400">ملاعب حقيقية من منشآت مسجّلة الآن — تصفّح وتواصل مباشر عبر واتساب</p>
        </div>
      </div>

      <div className="mb-5 rounded-2xl border border-white/10 bg-[#16233B] p-4" data-testid="explore-filters-bar">
        <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-[#1E2D4A] px-3 py-2.5">
          <Search className="h-4 w-4 shrink-0 text-slate-400" />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)} placeholder="ابحث باسم الملعب أو المنشأة"
            data-testid="explore-search-input" className="w-full bg-transparent text-sm text-white placeholder:text-slate-500 focus:outline-none"
          />
        </div>

        <div className="mt-3 flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar sm:flex-wrap sm:overflow-visible sm:pb-0" data-testid="explore-filters-row">
          <SelectFilter icon={MapPin} value={city} onChange={(e) => setCity(e.target.value)} testId="explore-city-filter">
            <option value="">كل المدن</option>
            {cities.map((c) => <option key={c} value={c}>{c}</option>)}
          </SelectFilter>

          <SelectFilter icon={Dumbbell} value={sportType} onChange={(e) => setSportType(e.target.value)} testId="explore-sport-filter">
            <option value="">كل الأنواع</option>
            {sportTypes.map((s) => <option key={s} value={s}>{s}</option>)}
          </SelectFilter>

          <SelectFilter icon={ArrowUpDown} value={sort} onChange={(e) => setSort(e.target.value)} testId="explore-sort-filter">
            {SORT_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </SelectFilter>

          <span aria-hidden className="mx-0.5 hidden h-6 w-px shrink-0 bg-white/10 sm:block" />

          <div className="flex shrink-0 gap-1.5" data-testid="explore-price-filters">
            {PRICE_RANGES.map((r) => (
              <button
                key={r.key} type="button" onClick={() => setPriceKey(r.key)} data-testid={`explore-price-${r.key || 'all'}`}
                className={`shrink-0 rounded-full px-3.5 py-2 text-xs font-bold transition-all ${priceKey === r.key ? 'bg-teal-400 text-[#0A192F] shadow-md shadow-teal-500/20' : 'border border-white/15 text-slate-300 hover:border-teal-400/50'}`}
              >
                {r.label}
              </button>
            ))}
          </div>

          <button
            type="button" onClick={() => setWomenOnly((v) => !v)} data-testid="explore-women-toggle"
            className={`shrink-0 rounded-full px-3.5 py-2 text-xs font-bold transition-all ${womenOnly ? 'bg-pink-500 text-white shadow-md shadow-pink-500/20' : 'border border-white/15 text-slate-300 hover:border-pink-400/50'}`}
          >
            مناسب للسيدات
          </button>

          {hasActiveFilters && (
            <button type="button" onClick={clearFilters} data-testid="explore-clear-filters" className="flex shrink-0 items-center gap-1 rounded-full px-3 py-2 text-xs font-bold text-slate-400 hover:text-white">
              <X className="h-3.5 w-3.5" /> إعادة ضبط
            </button>
          )}
        </div>
      </div>

      {venues !== null && (
        <p className="-mt-2 mb-4 text-xs text-slate-500" data-testid="explore-results-count">{venues.length} ملعب متاح</p>
      )}

      {venues === null ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">{[1, 2, 3].map((i) => <div key={i} className="h-64 animate-pulse rounded-2xl bg-white/5" />)}</div>
      ) : venues.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 p-10 text-center text-sm text-slate-500" data-testid="explore-empty-state">
          لا توجد ملاعب منشورة تطابق بحثك حالياً
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="explore-venues-grid">
          {venues.map((v) => <VenueCard key={v.id} venue={v} onClick={() => openVenue(v.id)} />)}
        </div>
      )}

      {selectedVenue && (
        <VenueDetailModal venue={selectedVenue} onClose={() => { setSelectedId(null); setSelectedVenue(null); }} />
      )}
    </div>
  );
}
